import { useRef, useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import SectionDivider from './_sectionDivider';
import IndicativeRegulatoryReport, { IScore } from './_indicativeRegulatoryReport';
import { AddressDto, KosmosReportDto } from 'models/dtoTypes';
import Section from './_section';
import SdgReport from './_sdgReport';
import DualUseGoodsReport from './_dualUseGoodsReport';
import IllegalLoggingGoodsReport from './_illegalLoggingReport';
import DoNoSignificantHarmDetailReport from './_doNoSignificantHarmDetailReport';
import EuTaxonomyReport from './_euTaxonomyReport';
import SustainableFinanceDisclosureRegulationReport from './_sustainableFinanceDisclosureRegulationReport';
import GreenHouseGasReport from './_greenHouseGasReport';
import NewsCheckReport from './_newsCheckReport';
import GovernanceReport from './_governanceReport';
import CapexOpexReport from './_capexOpexReport';
// import CompanyPolicySubmissionReport from './_companyPolicySubmissionReport';
// import FinalOverviewReport from './_finalOverviewReport';
import Appendix from './_appendix';
import TopActionBar from './_topActionBar';
import PageHeader from './_pageHeader';
import { LoadingRoundButton, RoundButton } from './_colorButton';
// import { savePdf } from './download';
import downloadKosmosReport from './downloadKosmosReport';
import { useTranslation } from 'react-i18next';
import ModernSlaveryReport from './_modernSlaveryReport';

const PREFIX = 'CombinedCompanyReport';

const classes = {
  tabHeaders: `${PREFIX}-tabHeaders`,
  tabRoot: `${PREFIX}-tabRoot`,
  tabSelected: `${PREFIX}-tabSelected`,
  links: `${PREFIX}-links`,
  linkContainer: `${PREFIX}-linkContainer`,
  floatBtnContainer: `${PREFIX}-floatBtnContainer`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  fontWeight: 'regular',
  padding: theme.spacing(6),
  [`& .${classes.tabHeaders}`]: {},
  [`& .${classes.tabRoot}`]: {
    height: 70,
    fontSize: 20,
  },
  [`& .${classes.tabSelected}`]: {
    fontWeight: 'bold',
  },
  [`& .${classes.links}`]: {
    display: 'flex',
  },
  [`& .${classes.linkContainer}`]: {
    flex: 1,
  },
  [`& .${classes.floatBtnContainer}`]: {
    position: 'fixed',
    display: 'flex',
    right: theme.spacing(6),
    bottom: theme.spacing(6),
    zIndex: 1001,
  },
}));

interface IProps {
  id: string;
  organisationId?: string;
  onBack?: () => void;
  onFetchReport: (id: string) => Promise<KosmosReportDto[]>;
}

export default function CombinedCompanyReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { id, organisationId, onBack, onFetchReport } = props;
  const [loading, setLoading] = useState(false);
  const [multipleReports, setMultipleReports] = useState<KosmosReportDto[]>();
  const kosmosReport = multipleReports && multipleReports[0];
  const smartGovernanceReport = multipleReports && multipleReports[1];
  const elementRef = useRef(null);
  const handleSave = async (): Promise<void> => {
    // [Company Name] - Coriolis Sustainability Report - AAAA-BBBB-001.pdf
    const reportDate = kosmosReport?.reportMetadata?.reportDate || id;
    const name = kosmosReport?.companyMetadata?.name || 'name unidentified';
    const filename = `${name} - Coriolis Sustainability Report -${reportDate}.pdf`;
    // await savePdf(elementRef.current, filename);
    await downloadKosmosReport({
      companyId: id,
      organisationId,
      defaultFilename: filename,
    });
    // saveImage(elementRef.current);
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    const fetchKosmosReport = async () => {
      setLoading(true);
      try {
        const reports = await onFetchReport(id);
        if (reports.length == 2) {
          setMultipleReports(reports);
        } else {
          throw new Error(t('two-reports-are-expected'));
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    void fetchKosmosReport();
  }, [id]);

  const generateFullAddress = (address?: AddressDto): string => {
    if (!address) {
      return t('unknown');
    }
    const { addressLine1, addressLine2, city, country, postalCode } = address;
    const addrComponents = [addressLine1, addressLine2, city, country, postalCode];
    return addrComponents.filter((c) => c).join(', ');
  };

  const indicativeRegulatoryReport = useMemo(() => {
    const { name, companyAddress, companyAddressInFull, companyRegistrationNumber } =
      kosmosReport?.companyMetadata || {};
    const automatedMetadata = kosmosReport?.reportMetadata || {};
    const automatedScore: IScore = {
      kosmosEsgScore: automatedMetadata.kosmosEsgScore || 0,
      kosmosEsgEnvironmentScore: automatedMetadata.kosmosEsgEnvironmentScore,
      kosmosEsgSocialScore: automatedMetadata.kosmosEsgSocialScore,
      kosmosEsgGovernanceScore: automatedMetadata.kosmosEsgGovernanceScore,
      reportDate: automatedMetadata.reportDate,
    };
    const smartGovernanceMetadata = smartGovernanceReport?.reportMetadata || {};
    const smartGovernanceScore: IScore = {
      kosmosEsgScore: smartGovernanceMetadata.kosmosEsgScore || 0,
      kosmosEsgEnvironmentScore: smartGovernanceMetadata.kosmosEsgEnvironmentScore,
      kosmosEsgSocialScore: smartGovernanceMetadata.kosmosEsgSocialScore,
      kosmosEsgGovernanceScore: smartGovernanceMetadata.kosmosEsgGovernanceScore,
      reportDate: smartGovernanceMetadata.reportDate,
    };
    return {
      name: name || '',
      address: companyAddressInFull || generateFullAddress(companyAddress),
      companyRegistrationNumber: companyRegistrationNumber || '',
      reportNumber: kosmosReport?.reportNumber || '',
      isSmartGovVerified: automatedMetadata?.isSmartGovVerified ?? false,
      automatedScore,
      smartGovernanceScore,
    };
  }, [kosmosReport, smartGovernanceReport]);

  const onScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (loading) {
    return (
      <Root>
        <CircularProgress />
      </Root>
    );
  }

  if (!kosmosReport) {
    return (
      <Root>
        <TopActionBar onBack={onBack} />
      </Root>
    );
  }

  const {
    adverseNewsReport,
    sdgReport,
    dualUseGoodsReport,
    illegalLoggingReport,
    doesNoSignificantHarmDetailReport,
    euTaxonomyReport,
    sustainableFinanceDisclosureRegulationReport,
    governanceReport,
    ghgScopeReport,
    ghgScopeSupplyChainAppendixReport,
  } = kosmosReport;
  const {
    sdgReport: sdgReportSg,
    dualUseGoodsReport: dualUseGoodsReportSg,
    modernSlaveryReport: modernSlaveryReportSg,
    illegalLoggingReport: illegalLoggingReportSg,
    doesNoSignificantHarmDetailReport: doesNoSignificantHarmDetailReportSg,
    euTaxonomyReport: euTaxonomyReportSg,
    sustainableFinanceDisclosureRegulationReport: sustainableFinanceDisclosureRegulationReportSg,
    governanceReport: governanceReportSg,
    ghgScopeReport: ghgScopeReportSg,
    ghgScopeSupplyChainAppendixReport: ghgScopeSupplyChainAppendixReportSg,
    companyQuestionnaireAnswers,
  } = smartGovernanceReport || {};
  const capexOpexReport = kosmosReport.capexOpexReport || [];
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  // <DualUseGoodsReport dualUseGoodsReport={kosmosReport?.dualUseGoodsReport || []}/>
  return (
    <Root ref={elementRef}>
      <div className="no-print">
        <TopActionBar onBack={onBack} id={id} />
        <PageHeader
          name={indicativeRegulatoryReport.name}
          address={indicativeRegulatoryReport.address}
          companyRegistrationNumber={indicativeRegulatoryReport.companyRegistrationNumber}
          isSmartGovVerified={indicativeRegulatoryReport.isSmartGovVerified}
        />
      </div>

      <Section title={t('indicative-regulatory-report')}>
        <IndicativeRegulatoryReport
          name={indicativeRegulatoryReport.name}
          address={indicativeRegulatoryReport.address}
          companyRegistrationNumber={indicativeRegulatoryReport.companyRegistrationNumber}
          automatedScore={indicativeRegulatoryReport.automatedScore}
          smartGovernanceScore={indicativeRegulatoryReport.smartGovernanceScore}
          reportId={indicativeRegulatoryReport.reportNumber}
        />
      </Section>

      <Section title={t('an-automated-check-of-this-company-has-been-found')}>
        <>
          <SectionDivider id="sustainable-development-goals" />
          <SdgReport
            sdgReport={sdgReport || []}
            sdgReportSmartGovernance={sdgReportSg || undefined}
          />
          <SectionDivider id="dual-use-goods-check" />
          <DualUseGoodsReport
            dualUseGoodsReport={dualUseGoodsReport || []}
            dualUseGoodsReportSmartGovernance={dualUseGoodsReportSg || undefined}
          />
          <SectionDivider id="modern-slavery" />
          <ModernSlaveryReport modernSlaveryReport={modernSlaveryReportSg || []} />
          <SectionDivider id="illegal-logging-check" />
          <IllegalLoggingGoodsReport
            illegalLoggingReport={illegalLoggingReport || []}
            illegalLoggingReportSmartGovernance={illegalLoggingReportSg || undefined}
          />
          <SectionDivider id="eu-taxonomy-score" />
          <EuTaxonomyReport
            euTaxonomyReport={euTaxonomyReport || []}
            euTaxonomyReportSmartGovernance={euTaxonomyReportSg || undefined}
          />
          <SectionDivider id="eu-taxonomy-regulation-dhsh" />
          <DoNoSignificantHarmDetailReport
            doNoSignificantHarmDetailReport={doesNoSignificantHarmDetailReport || []}
            doNoSignificantHarmDetailReportSmartGovernance={
              doesNoSignificantHarmDetailReportSg || undefined
            }
          />
          <SectionDivider id="sustainable-finance-disclosure-regulation" />
          <SustainableFinanceDisclosureRegulationReport
            sustainableFinanceDisclosureRegulationReport={
              sustainableFinanceDisclosureRegulationReport || []
            }
            sustainableFinanceDisclosureRegulationReportSmartGovernance={
              sustainableFinanceDisclosureRegulationReportSg || undefined
            }
          />
          <SectionDivider id="greenhouse-gases" />
          <GreenHouseGasReport
            ghgScopeReport={ghgScopeReport || []}
            ghgScopeReportSmartGovernance={ghgScopeReportSg || undefined}
          />
          <SectionDivider id="news-check" />
          <NewsCheckReport
            adverseNewsReport={adverseNewsReport || []}
            enableBarChart={true}
            enableTrendChart={false}
          />
          <SectionDivider id="governance" />
          <GovernanceReport
            negativeGovernance={governanceReport?.NegativeGovernance || []}
            positiveGovernance={governanceReport?.PositiveGovernance || []}
            negativeGovernanceSmartGovernance={governanceReportSg?.NegativeGovernance || []}
            positiveGovernanceSmartGovernance={governanceReportSg?.PositiveGovernance || []}
          />
        </>
      </Section>
      {capexOpexReport.length > 0 && (
        <Section id="self-reported-information" title="Self reported information">
          <CapexOpexReport capexOpexReport={capexOpexReport} />
        </Section>
      )}
      {/* {
         <CompanyPolicySubmissionReport companyPolicySubmissionReport={companyPolicySubmissionReport || []} />
         <FinalOverviewReport
           name={indicativeRegulatoryReport.name}
           score={indicativeRegulatoryReport.score}
         />
      } */}
      <div id="appendix">
        <Appendix
          sdgReport={sdgReport || []}
          sdgReportSmartGovernance={sdgReportSg || undefined}
          ghgScopeSupplyChainAppendixReport={ghgScopeSupplyChainAppendixReport || []}
          ghgScopeSupplyChainAppendixReportSmartGovernance={
            ghgScopeSupplyChainAppendixReportSg || undefined
          }
          isSmartGovVerified={indicativeRegulatoryReport.isSmartGovVerified}
          companyQuestionnaireDetail={
            companyQuestionnaireAnswers?.companyQuestionAnswerPairDtos || []
          }
        />
      </div>
      <div className={classes.floatBtnContainer}>
        <LoadingRoundButton onClick={handleSave} />
        <RoundButton onClick={onScrollToTop} />
      </div>
    </Root>
  );
}
